import { useEffect, useState } from 'react';
import { http } from '../../lib/api';
import { AppLayout } from '../../components/layout';
import { Button, Card, Input } from '../../components/core';
import { SpinnerBlock } from '../../components/feedback';
import { useBrandingStore, type PlatformBranding } from '../../store/branding';
import type { RenewalReminderSettings, MessagingSettings, RazorpaySettings } from '@livestreamx/shared-types';
import { ADMIN_NAV } from './nav';

const DEF: PlatformBranding = {
  platformName: 'LiveStreamX', logoUrl: null, brandColor: null, defaultPlayerTheme: 'dark',
  faviconUrl: null, loginTitle: null, loginTagline: null, footerText: null, supportEmail: null,
  address: null, supportHours: null, anotherPhone: null,
  supportWhatsapp: null,
};

const DEF_REN: RenewalReminderSettings = {
  whatsappTemplate:
    'Hi {tenant}, your {plan} plan (₹{amount}) renews on {renewsOn} ({days} days).\n' +
    'Please review your billing anytime at {platform} — reply here if you need anything.',
  emailSubject: 'Upcoming renewal — your {plan} plan',
  emailBody:
    'Hi {tenant},\n\nYour {plan} plan (₹{amount}) renews on {renewsOn}.\n\n' +
    'If you have any questions or want to make changes, just reply to this email.\n\n— {platform}',
  reminderDays: 30,
};

const PLACEHOLDERS = '{tenant} {plan} {amount} {currency} {renewsOn} {days} {platform}';

const DEF_MSG: MessagingSettings = {
  whatsappSender: null,
  smtpHost: null,
  smtpPort: null,
  smtpUser: null,
  smtpPassword: '',
  smtpFrom: null,
  smtpConfigured: false,
};

const DEF_RZ: RazorpaySettings = {
  razorpayKeyId: null,
  razorpayKeySecret: '',
  razorpayWebhookSecret: '',
  currency: 'INR',
  configured: false,
};

export function AdminSettingsPage() {
  const branding = useBrandingStore();
  const [f, setF] = useState(DEF);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [msg, setMsg] = useState('');

  const [ren, setRen] = useState(DEF_REN);
  const [renLoading, setRenLoading] = useState(true);
  const [renSaving, setRenSaving] = useState(false);
  const [renDirty, setRenDirty] = useState(false);
  const [renMsg, setRenMsg] = useState('');

  const [msgCfg, setMsgCfg] = useState(DEF_MSG);
  const [msgLoading, setMsgLoading] = useState(true);
  const [msgSaving, setMsgSaving] = useState(false);
  const [msgDirty, setMsgDirty] = useState(false);
  const [msgSaveMsg, setMsgSaveMsg] = useState('');

  const [rzCfg, setRzCfg] = useState(DEF_RZ);
  const [rzLoading, setRzLoading] = useState(true);
  const [rzSaving, setRzSaving] = useState(false);
  const [rzDirty, setRzDirty] = useState(false);
  const [rzSaveMsg, setRzSaveMsg] = useState('');

  useEffect(() => {
    http.get<PlatformBranding>('/api/platform/branding')
      .then((d) => { setF({ ...DEF, ...d }); setLoading(false); })
      .catch(() => setLoading(false));
    http.get<RenewalReminderSettings>('/api/platform/renewals')
      .then((d) => { setRen({ ...DEF_REN, ...d }); setRenLoading(false); })
      .catch(() => setRenLoading(false));
    http.get<MessagingSettings>('/api/platform/messaging')
      .then((d) => { setMsgCfg({ ...DEF_MSG, ...d }); setMsgLoading(false); })
      .catch(() => setMsgLoading(false));
    http.get<RazorpaySettings>('/api/platform/payments')
      .then((d) => { setRzCfg({ ...DEF_RZ, ...d }); setRzLoading(false); })
      .catch(() => setRzLoading(false));
  }, []);

  const set = <K extends keyof PlatformBranding>(key: K, val: PlatformBranding[K]) => {
    setF((p) => ({ ...p, [key]: val })); setDirty(true); setMsg('');
  };

  const uploadBranding = async (kind: 'logo' | 'favicon', file: File | null) => {
    if (!file) return;
    const setUploading = kind === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await http.post<{ url: string }>(`/api/platform/branding/upload?type=${kind}`, fd);
      set(kind === 'logo' ? 'logoUrl' : 'faviconUrl', res.url);
    } catch (e) {
      setMsg('Error: ' + ((e as Error).message ?? 'Failed'));
    } finally {
      setUploading(false);
    }
  };
  const patchRen = <K extends keyof RenewalReminderSettings>(key: K, val: RenewalReminderSettings[K]) => {
    setRen((p) => ({ ...p, [key]: val })); setRenDirty(true); setRenMsg('');
  };

  const patchMsg = <K extends keyof MessagingSettings>(key: K, val: MessagingSettings[K]) => {
    setMsgCfg((p) => ({ ...p, [key]: val })); setMsgDirty(true); setMsgSaveMsg('');
  };

  const save = async () => {
    setSaving(true);
    try { await http.patch('/api/platform/branding', f); setDirty(false); setMsg('Saved'); branding.load(); }
    catch (e) { setMsg('Error: ' + ((e as Error).message ?? 'Failed')); }
    setSaving(false);
  };

  const saveRen = async () => {
    setRenSaving(true);
    try {
      const d = await http.patch<RenewalReminderSettings>('/api/platform/renewals', ren);
      setRen({ ...DEF_REN, ...d }); setRenDirty(false); setRenMsg('Saved');
    } catch (e) { setRenMsg('Error: ' + ((e as Error).message ?? 'Failed')); }
    setRenSaving(false);
  };

  const saveMsg = async () => {
    setMsgSaving(true);
    try {
      const d = await http.patch<MessagingSettings>('/api/platform/messaging', msgCfg);
      setMsgCfg({ ...DEF_MSG, ...d, smtpPassword: '' }); setMsgDirty(false); setMsgSaveMsg('Saved');
    } catch (e) { setMsgSaveMsg('Error: ' + ((e as Error).message ?? 'Failed')); }
    setMsgSaving(false);
  };

  const discardMsg = async () => {
    try {
      const d = await http.get<MessagingSettings>('/api/platform/messaging');
      setMsgCfg({ ...DEF_MSG, ...d }); setMsgDirty(false); setMsgSaveMsg('');
    } catch { /* noop */ }
  };

  const patchRz = <K extends keyof RazorpaySettings>(key: K, val: RazorpaySettings[K]) => {
    setRzCfg((p) => ({ ...p, [key]: val })); setRzDirty(true); setRzSaveMsg('');
  };

  const saveRz = async () => {
    setRzSaving(true);
    try {
      const d = await http.patch<RazorpaySettings>('/api/platform/payments', rzCfg);
      setRzCfg({ ...DEF_RZ, ...d, razorpayKeySecret: '', razorpayWebhookSecret: '' });
      setRzDirty(false); setRzSaveMsg('Saved');
    } catch (e) { setRzSaveMsg('Error: ' + ((e as Error).message ?? 'Failed')); }
    setRzSaving(false);
  };

  const discardRz = async () => {
    try {
      const d = await http.get<RazorpaySettings>('/api/platform/payments');
      setRzCfg({ ...DEF_RZ, ...d }); setRzDirty(false); setRzSaveMsg('');
    } catch { /* noop */ }
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="settings-page">
        <div className="page-header admin-page-header">
          <div><h1 className="page-title">Settings</h1><p className="page-subtitle">Platform branding and white-label config</p></div>
        </div>
        {loading ? <SpinnerBlock /> : (
          <Card title="Branding">
            <div className="settings-grid">
              <Input label="Platform Name" value={f.platformName} onChange={(v) => set('platformName', v)} />
              <Input label="Support Email" type="email" value={f.supportEmail ?? ''} onChange={(v) => set('supportEmail', v || null)} />
               <Input label="Address" value={f.address ?? ''} onChange={(v) => set('address', v || null)} hint="Business address shown on the landing page" />
               <Input label="Support Hours" value={f.supportHours ?? ''} onChange={(v) => set('supportHours', v || null)} hint="e.g. Mon–Fri, 9am–6pm IST" />
               <Input label="Another Phone" value={f.anotherPhone ?? ''} onChange={(v) => set('anotherPhone', v || null)} hint="Alternate contact number" />
              <div className="field full branding-asset">
                <label>Logo</label>
                <div className="branding-asset-row">
                  <div className="branding-asset-preview">
                    {f.logoUrl
                      ? <img src={f.logoUrl} alt="Logo preview" />
                      : <span className="text-muted text-xs">No logo</span>}
                  </div>
                  <div className="branding-asset-controls">
                    <input type="file" accept="image/*" disabled={uploadingLogo} onChange={(e) => uploadBranding('logo', e.target.files?.[0] ?? null)} />
                    {uploadingLogo && <span className="text-sm text-muted">Uploading…</span>}
                    <span className="text-muted text-xs">PNG, SVG, JPG or WebP · max 5 MB</span>
                  </div>
                </div>
                <Input label="Logo URL" value={f.logoUrl ?? ''} onChange={(v) => set('logoUrl', v || null)} hint="Full URL or uploaded path" />
              </div>
              <div className="field full branding-asset">
                <label>Favicon</label>
                <div className="branding-asset-row">
                  <div className="branding-asset-preview">
                    {f.faviconUrl
                      ? <img src={f.faviconUrl} alt="Favicon preview" />
                      : <span className="text-muted text-xs">No favicon</span>}
                  </div>
                  <div className="branding-asset-controls">
                    <input type="file" accept="image/*" disabled={uploadingFavicon} onChange={(e) => uploadBranding('favicon', e.target.files?.[0] ?? null)} />
                    {uploadingFavicon && <span className="text-sm text-muted">Uploading…</span>}
                    <span className="text-muted text-xs">ICO, PNG or SVG · max 5 MB</span>
                  </div>
                </div>
                <Input label="Favicon URL" value={f.faviconUrl ?? ''} onChange={(v) => set('faviconUrl', v || null)} hint="Full URL or uploaded path" />
              </div>
              <div className="color-row">
                <Input label="Brand Color" value={f.brandColor ?? ''} onChange={(v) => set('brandColor', v || null)} hint="#hex" />
                <input type="color" className="color-swatch" value={f.brandColor ?? '#6366f1'} onChange={(e) => set('brandColor', e.target.value)} />
              </div>
              <Input label="Player Theme" value={f.defaultPlayerTheme} onChange={(v) => set('defaultPlayerTheme', v)} hint="dark or light" />
              <Input label="Login Title" value={f.loginTitle ?? ''} onChange={(v) => set('loginTitle', v || null)} />
              <Input label="Login Tagline" value={f.loginTagline ?? ''} onChange={(v) => set('loginTagline', v || null)} />
              <div className="field full">
                <label>Footer Text</label>
                <input className="input" value={f.footerText ?? ''} onChange={(e) => set('footerText', e.target.value || null)} />
              </div>
            </div>
          </Card>
        )}
        {renLoading ? <SpinnerBlock /> : (
          <Card title="Renewal Reminders">
            <p className="text-muted text-sm" style={{ margin: '0 0 14px' }}>
              Message templates for the Upcoming Renewals dashboard. WhatsApp opens a wa.me draft on the tenant's phone number; email is sent via SMTP.
              Placeholders: <code className="text-sm">{PLACEHOLDERS}</code>
            </p>
            <div className="settings-grid">
              <Input
                label="Reminder Window (days)"
                type="number"
                value={String(ren.reminderDays)}
                onChange={(v) => patchRen('reminderDays', Number(v) || 30)}
                hint="Subscriptions renewing within this window appear on the dashboard (1–90)"
              />
              <Input
                label="Email Subject"
                value={ren.emailSubject}
                onChange={(v) => patchRen('emailSubject', v)}
              />
              <div className="field full">
                <label>WhatsApp Message Template</label>
                <textarea className="input" rows={5} value={ren.whatsappTemplate} onChange={(e) => patchRen('whatsappTemplate', e.target.value)} />
              </div>
              <div className="field full">
                <label>Email Body Template</label>
                <textarea className="input" rows={8} value={ren.emailBody} onChange={(e) => patchRen('emailBody', e.target.value)} />
              </div>
            </div>
            <div className="save-bar">
              <span className="text-sm">{renDirty ? 'Unsaved changes' : 'Configured'}</span>
              <div className="save-bar-actions">
                {renMsg && <span className="text-sm" style={{ color: renMsg.startsWith('Error') ? 'var(--danger)' : 'var(--success)' }}>{renMsg}</span>}
                <Button variant="secondary" onClick={() => { setRen(DEF_REN); setRenDirty(false); }}>Discard</Button>
                <Button onClick={saveRen} disabled={renSaving || !renDirty}>{renSaving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          </Card>
        )}
        {msgLoading ? <SpinnerBlock /> : (
          <Card title="WhatsApp & Email (SMTP)">
            <p className="text-muted text-sm" style={{ margin: '0 0 14px' }}>
              Outbound sender setup. WhatsApp reminders open a wa.me draft from your own WhatsApp — the sender number below is stored as the
              platform's business identity. Email goes out via this SMTP server when configured, otherwise it is logged to the API console (dev mode).
            </p>
            <div className="settings-grid">
              <Input
                label="WhatsApp Sender Number"
                value={msgCfg.whatsappSender ?? ''}
                onChange={(v) => patchMsg('whatsappSender', v || null)}
                placeholder="+91 98765 43210"
              />
              <Input
                label="SMTP Host"
                value={msgCfg.smtpHost ?? ''}
                onChange={(v) => patchMsg('smtpHost', v || null)}
                placeholder="smtp.example.com"
              />
              <Input
                label="SMTP Port"
                type="number"
                value={msgCfg.smtpPort == null ? '' : String(msgCfg.smtpPort)}
                onChange={(v) => patchMsg('smtpPort', v === '' ? null : Number(v))}
                hint="465 = SSL, 587 = STARTTLS"
              />
              <Input
                label="SMTP Username"
                value={msgCfg.smtpUser ?? ''}
                onChange={(v) => patchMsg('smtpUser', v || null)}
              />
              <Input
                label="SMTP Password"
                type="password"
                value={msgCfg.smtpPassword}
                onChange={(v) => patchMsg('smtpPassword', v)}
                autoComplete="new-password"
                hint={msgCfg.smtpConfigured ? 'Saved — leave empty to keep' : 'Not set'}
              />
              <Input
                label="SMTP From"
                value={msgCfg.smtpFrom ?? ''}
                onChange={(v) => patchMsg('smtpFrom', v || null)}
                placeholder="LiveStreamX &lt;no-reply@example.com&gt;"
              />
            </div>
            <div className="save-bar">
              <span className="text-sm">{msgCfg.smtpConfigured || (!!msgCfg.smtpHost && msgCfg.smtpPort != null) ? 'SMTP configured' : 'No SMTP — emails logged to API console'}</span>
              <div className="save-bar-actions">
                {msgSaveMsg && <span className="text-sm" style={{ color: msgSaveMsg.startsWith('Error') ? 'var(--danger)' : 'var(--success)' }}>{msgSaveMsg}</span>}
                <Button variant="secondary" onClick={discardMsg}>Discard</Button>
                <Button onClick={saveMsg} disabled={msgSaving || !msgDirty}>{msgSaving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          </Card>
        )}
        {rzLoading ? <SpinnerBlock /> : (
          <Card title="Payment Gateway (Razorpay)">
            <p className="text-muted text-sm" style={{ margin: '0 0 14px' }}>
              Razorpay checkout keys. When saved here they override the server's RAZORPAY_* environment variables. Secrets are write-only —
              leave a secret field empty to keep the stored value. Tenants can enable or disable the gateway from their Billing page.
            </p>
            <div className="settings-grid">
              <Input
                label="Key ID"
                value={rzCfg.razorpayKeyId ?? ''}
                onChange={(v) => patchRz('razorpayKeyId', v || null)}
                placeholder="rzp_test_..."
              />
              <Input
                label="Key Secret"
                type="password"
                value={rzCfg.razorpayKeySecret}
                onChange={(v) => patchRz('razorpayKeySecret', v)}
                autoComplete="new-password"
                hint={rzCfg.configured ? 'Saved — leave empty to keep' : 'Not set'}
              />
              <Input
                label="Webhook Secret"
                type="password"
                value={rzCfg.razorpayWebhookSecret}
                onChange={(v) => patchRz('razorpayWebhookSecret', v)}
                autoComplete="new-password"
                hint="Verifies payment.captured / failed / refunded events"
              />
              <Input
                label="Currency"
                value={rzCfg.currency}
                onChange={(v) => patchRz('currency', v || null)}
                placeholder="INR"
                hint="ISO 4217, e.g. INR, USD, AED"
              />
            </div>
            <div className="save-bar">
              <span className="text-sm">
                {rzCfg.configured
                  ? 'Keys stored — overriding server env'
                  : 'No keys stored — falling back to server RAZORPAY_* (orders rejected if those are empty too)'}
              </span>
              <div className="save-bar-actions">
                {rzSaveMsg && <span className="text-sm" style={{ color: rzSaveMsg.startsWith('Error') ? 'var(--danger)' : 'var(--success)' }}>{rzSaveMsg}</span>}
                <Button variant="secondary" onClick={discardRz}>Discard</Button>
                <Button onClick={saveRz} disabled={rzSaving || !rzDirty}>{rzSaving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          </Card>
        )}
        {dirty && (
          <div className="save-bar">
            <span className="text-sm">Unsaved changes</span>
            <div className="save-bar-actions">
              {msg && <span className="text-sm" style={{ color: msg.startsWith('Error') ? 'var(--danger)' : 'var(--success)' }}>{msg}</span>}
              <Button variant="secondary" onClick={() => { setF(DEF); setDirty(false); }}>Discard</Button>
              <Button onClick={save} disabled={saving || uploadingLogo || uploadingFavicon}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
